import React, { useEffect, useState } from "react";

import { Editor } from "@monaco-editor/react";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import DataTable from "components/basic/data-table/DataTable";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { Button } from "primereact/button";
import { ColumnBodyOptions } from "primereact/column";
import { DataTableExpandedRows, DataTableProps, DataTableValueArray } from "primereact/datatable";
import { InputNumber } from "primereact/inputnumber";
import { T } from "services/lang/LanguageService";
import { v4 } from "uuid";
import { Dropdown } from "primereact/dropdown";
import { ScadaFreeText, ScadaProps } from "../scadaTypes";
import { defaultFreeText } from "../utils/default-values";

type ScadaFreeTextProps = {
  currentConfig: ScadaProps;
  setCurrentConfig: React.Dispatch<React.SetStateAction<ScadaProps>>;
  onConfigChange: (config: ScadaProps) => void;
};

const ScadaFreeTextTable: React.FC<ScadaFreeTextProps> = ({
  currentConfig,
  setCurrentConfig,
  onConfigChange,
}) => {
  const [freeTexts, setFreeTexts] = useState<ScadaFreeText[]>(() => currentConfig.freeTexts ?? []);
  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  useEffect(() => {
    setCurrentConfig({ ...currentConfig, freeTexts });
    onConfigChange({ ...currentConfig, freeTexts });
  }, [freeTexts]);

  const handleChange = (
    value: string | number | null | undefined,
    field: keyof ScadaFreeText,
    data: ScadaFreeText
  ) => {
    setFreeTexts(prev => prev.map(p => (p.id === data.id ? { ...p, [field]: value } : p)));
  };

  const renderCoordinate = (data: ScadaFreeText, options: ColumnBodyOptions) => (
    <InputNumber
      value={data[options.field as "x" | "y"]}
      onChange={e => handleChange(e.value, options.field as "x" | "y", data)}
      name={options.field!}
      style={{ minWidth: "4rem" }}
      maxFractionDigits={2}
      max={100}
      min={0}
    />
  );
  const renderColor = (data: ScadaFreeText) => (
    <ColorPicker
      value={data.color}
      onValueChange={e => handleChange(e.target.value, "color", data)}
      name="color"
    />
  );

  const renderFontSize = (data: ScadaFreeText) => (
    <InputNumber
      className="justify-self-end"
      style={{ minWidth: "4rem" }}
      value={data.fontSize}
      name="fontSize"
      onChange={e => handleChange(e.value, "fontSize", data)}
      maxFractionDigits={2}
      max={120}
      min={0}
    />
  );

  const rowExpansionTemplate = (data: ScadaFreeText) => (
    <Editor
      language="html"
      theme="vs-dark"
      value={data.text || ""}
      height="200px"
      onChange={value => handleChange(value, "text", data)}
      options={{ formatOnPaste: true }}
    />
  );

  const renderLayerField = (data: ScadaFreeText) => (
    <Dropdown
      value={data.layer}
      onChange={e => handleChange(e.value, "layer", data)}
      style={{
        width: "max(8rem, 10rem)",
        padding: "0",
      }}
      showClear
      className="p-inputtext-sm align-items-center"
      options={[...(currentConfig.labelLayers ?? []).map(lg => lg.layer)]}
    />
  );

  const renderRemove = (rowData: ScadaFreeText) => (
    <Button
      type="button"
      icon="pi pi-trash"
      className="p-button-sm p-button-text p-button-danger p-1"
      style={{ width: "1.9rem" }}
      onClick={() => setFreeTexts(prev => prev.filter(p => p.id !== rowData.id))}
    />
  );

  const columns: TableColumn<ScadaFreeText>[] = [
    {
      columnKey: "expander",
      header: "",
      expander: true,
      style: { width: "3rem" },
    },
    {
      columnKey: "num",
      header: "No",
      body: (_, options) => options.rowIndex + 1,
    },
    {
      field: "x",
      columnKey: "x",
      header: "X, %",
      body: renderCoordinate,
    },
    {
      field: "y",
      columnKey: "y",
      header: "Y, %",
      body: renderCoordinate,
    },
    {
      field: "color",
      columnKey: "color",
      header: T("word.color"),
      body: renderColor,
    },
    {
      field: "fontSize",
      columnKey: "fontSize",
      header: T("word.font-size"),
      body: renderFontSize,
    },
    {
      field: "layer",
      columnKey: "layer",
      header: T("word.layer"),
      body: renderLayerField,
    },
    {
      header: "",
      columnKey: "actions",
      body: renderRemove,
    },
  ];

  const tableConfig: DataTableProps<ScadaFreeText[]> = {
    header: (
      <div className="flex justify-content-end flex-wrap gap-2">
        <Button
          label={T("action.create-new")}
          type="button"
          icon="pi pi-plus"
          size="small"
          className="component-editor-button icon-green w-max"
          onClick={() => setFreeTexts([...freeTexts, { id: v4(), ...defaultFreeText }])}
        />
      </div>
    ),
    expandedRows,
    onRowToggle: e => setExpandedRows(e.data),
    rowExpansionTemplate,
    dataKey: "id",
  };

  return <DataTable columns={columns} data={freeTexts} tableConfig={tableConfig} />;
};

export default ScadaFreeTextTable;
